import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WalterLoader from '../../../../shared/WalterLoader';
import Stepper from '../../../../shared/Stepper.js';
import { DEFAULT_USAGES } from './calculations/monthlyNeedCalculations.js';
import { detectEligibleUsagesFromAudit } from './calculations/auditUsageImport.js';
import { roofsFromBuilding } from './calculations/roofCollectionCalculations.js';
import { DEFAULT_RAINFALL } from './calculations/meteoImport.js';
import { DEFAULT_WEEKDAY_PCT } from './calculations/tankSimulation.js';
import { computeBudget } from './calculations/budgetCalculations.js';
import { shouldRecommendSurpression } from './calculations/pompageCalculations.js';
import { defaultNbPointsEau } from './calculations/plomberieCalculations.js';
import { DEFAULT_WATER_PRICE } from './calculations/roiCalculations.js';
import NeedsStep from './steps/NeedsStep.js';
import RoofStep from './steps/RoofStep.js';
import RainfallStep from './steps/RainfallStep.js';
import BalanceStep from './steps/BalanceStep.js';
import TankSizingStep from './steps/TankSizingStep.js';
import BudgetStep from './steps/BudgetStep.js';
import RoiStep from './steps/RoiStep.js';
import '../../actionPlanV2.css';
import './rainwaterHarvesting.css';

const STEP_LABELS = [
  { label: 'Besoins' },
  { label: 'Toiture' },
  { label: 'Pluviométrie' },
  { label: 'Bilan' },
  { label: 'Cuve' },
  { label: 'Budget' },
  { label: 'ROI' },
];

const DEFAULT_TANK_PARAMS = { volMin: 1, volMax: 20, volStep: 1, volInitial: 0, targetReliability: 95 };

// budgetState est organisé par domaine (cuve/pompage/filtration/plomberie), une section
// accordéon chacun — chaque domaine porte son propre `refined` pour le mode "Affiner ce poste".
// La main-d'œuvre de pose est rattachée à Cuve (installation de la cuve elle-même), distincte
// du terrassement (excavation, cuve enterrée uniquement).
const DEFAULT_BUDGET_STATE = {
  cuve: { type: 'aerienne', volume: 3000, mainoeuvre: 'diy', refined: false, acces: 'facile', sol: 'meuble', distanceMaison: 0 },
  pompage: { mode: 'simple', surpressionDebit: 'd2', bascule: false },
  filtration: { moduleIds: ['tamis'] },
  plomberie: {
    nbDescentes: 1, distanceCanalisation: 0,
    plomberie: 'basique', poseType: 'apparent', nbPointsEau: 1,
    niveau: false, compteur: false, refined: false,
  },
};

/**
 * Module "Récupération d'eau de pluie" — wizard 6 étapes ; entièrement client-side et
 * éphémère (pas de sauvegarde backend), comme le module hydro-économe (HydroEfficientEquipmentModule.js).
 */
const RainwaterHarvestingModule = () => {
  const navigate = useNavigate();
  const { client_id, building_id } = useParams();

  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [usages, setUsages] = useState([]);
  const [roofs, setRoofs] = useState([]);
  const [rainfall, setRainfall] = useState(DEFAULT_RAINFALL);
  const [weekdayPct, setWeekdayPct] = useState(DEFAULT_WEEKDAY_PCT);
  const [tankParams, setTankParams] = useState(DEFAULT_TANK_PARAMS);
  const [selectedTankVolume, setSelectedTankVolume] = useState(null);
  const [selectedTankCoverage, setSelectedTankCoverage] = useState(null);
  const [budgetState, setBudgetState] = useState(DEFAULT_BUDGET_STATE);
  const [waterPrice, setWaterPrice] = useState(DEFAULT_WATER_PRICE);

  const [currentStep, setCurrentStep] = useState(0);
  const [furthestStepReached, setFurthestStepReached] = useState(0);

  /* ── Bâtiment (toiture, prix de l'eau) + équipements/usages réels de l'audit,
     pour pré-remplir l'étape Besoins avec les vrais volumes (WC, lave-linge, arrosage) ── */
  React.useEffect(() => {
    if (!building_id) return;
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/get_building/${building_id}`).then(r => {
        if (!r.ok) throw new Error("Erreur lors de la récupération du bâtiment.");
        return r.json();
      }),
      fetch(`${process.env.REACT_APP_API_URL}/load_equipments_with_rating/${building_id}`).then(r => {
        if (!r.ok) throw new Error("Erreur lors de la récupération des équipements.");
        return r.json();
      }),
    ])
      .then(([buildingData, equipmentsData]) => {
        setBuilding(buildingData);
        setRoofs(roofsFromBuilding(buildingData));
        return fetch(`${process.env.REACT_APP_API_URL}/load_usages_by_equipments/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(equipmentsData),
        })
          .then(r => { if (!r.ok) throw new Error("Erreur lors de la récupération des usages."); return r.json(); })
          .then(usagesData => {
            const detected = detectEligibleUsagesFromAudit(equipmentsData, usagesData);
            const resolvedUsages = detected.length > 0 ? detected : DEFAULT_USAGES;
            setUsages(resolvedUsages);
            // Pré-sélectionne le système de surpression + la bascule eau de ville si un usage
            // intérieur sous pression (WC/lave-linge) est actif — recommandation non bloquante,
            // librement modifiable. Pré-remplit aussi le nombre de points d'eau à partir des
            // usages WC/lave-linge actifs.
            const recommended = shouldRecommendSurpression(resolvedUsages);
            setBudgetState(prev => ({
              ...prev,
              pompage: { ...prev.pompage, mode: recommended ? 'surpression' : 'simple', bascule: recommended },
              plomberie: { ...prev.plomberie, nbPointsEau: defaultNbPointsEau(resolvedUsages) },
            }));
          });
      })
      .then(() => setLoading(false))
      .catch(err => { setError(err.message); setLoading(false); });
  }, [building_id]);

  const canAdvance = [
    true, // step 0: besoins — toujours avançable, même sans usage actif
    roofs.length > 0, // step 1: toiture
    true, // step 2: pluviométrie — valeurs par défaut toujours présentes
    true, // step 3: bilan — lecture seule
    selectedTankVolume != null, // step 4: cuve — il faut avoir choisi une cuve pour aller au budget
    true, // step 5: budget — toujours avançable vers le ROI
    false, // step 6: terminal
  ][currentStep];

  const goToStep = (index) => { if (index <= furthestStepReached) setCurrentStep(index); };

  const goNext = () => {
    if (currentStep === STEP_LABELS.length - 1) return;
    const next = currentStep + 1;
    setCurrentStep(next);
    setFurthestStepReached(f => Math.max(f, next));
  };

  const goPrevious = () => { if (currentStep > 0) setCurrentStep(s => s - 1); };

  const selectTank = (capacityM3, reliability) => {
    setSelectedTankVolume(capacityM3);
    setSelectedTankCoverage(reliability);
    let liters = Math.round(capacityM3 * 1000 / 500) * 500;
    liters = Math.min(100000, Math.max(500, liters));
    setBudgetState(prev => ({ ...prev, cuve: { ...prev.cuve, volume: liters } }));
    const next = 5;
    setCurrentStep(next);
    setFurthestStepReached(f => Math.max(f, next));
  };

  const budget = useMemo(() => computeBudget(budgetState), [budgetState]);

  if (loading) return (
    <div className="centered-spinner-dashboard">
      <WalterLoader />
    </div>
  );
  if (error) return <p>Erreur : {error}</p>;

  const stepProps = { currentStep, totalSteps: STEP_LABELS.length, goPrevious, goNext, canAdvance };

  return (
    <div className="apv2-page">
      <div className="apv2-module-header">
        <button type="button" className="back-button" onClick={() => navigate(`/client/${client_id}/${building_id}/action_plan_v2`)}>
          ← Modules de chiffrage
        </button>
      </div>

      <div className="apv2-wizard-shell">
        <h1 className="apv2-wizard-title">Récupération d'eau de pluie</h1>
        <div className="apv2-wizard-nav">
          <button type="button" className="apv2-wizard-nav-btn" onClick={goPrevious} disabled={currentStep === 0}>
            ← Précédent
          </button>
          <Stepper steps={STEP_LABELS} currentStep={currentStep} furthestStepReached={furthestStepReached} onStepClick={goToStep} />
          <button type="button" className="apv2-wizard-nav-btn" onClick={goNext} disabled={!canAdvance || currentStep === STEP_LABELS.length - 1}>
            Suivant →
          </button>
        </div>
      </div>

      {currentStep === 0 && <NeedsStep usages={usages} setUsages={setUsages} {...stepProps} />}
      {currentStep === 1 && <RoofStep roofs={roofs} setRoofs={setRoofs} {...stepProps} />}
      {currentStep === 2 && <RainfallStep rainfall={rainfall} setRainfall={setRainfall} roofs={roofs} {...stepProps} />}
      {currentStep === 3 && <BalanceStep usages={usages} roofs={roofs} rainfall={rainfall} {...stepProps} />}
      {currentStep === 4 && (
        <TankSizingStep
          usages={usages} roofs={roofs} rainfall={rainfall}
          weekdayPct={weekdayPct} setWeekdayPct={setWeekdayPct}
          tankParams={tankParams} setTankParams={setTankParams}
          selectedTankVolume={selectedTankVolume} selectedTankCoverage={selectedTankCoverage} onSelectTank={selectTank}
          {...stepProps}
        />
      )}
      {currentStep === 5 && (
        <BudgetStep
          usages={usages} budgetState={budgetState} setBudgetState={setBudgetState}
          selectedTankCoverage={selectedTankCoverage} waterPrice={waterPrice}
          {...stepProps}
        />
      )}
      {currentStep === 6 && (
        <RoiStep
          usages={usages}
          selectedTankVolume={selectedTankVolume} selectedTankCoverage={selectedTankCoverage}
          budgetState={budgetState}
          budgetTotalLow={budget.totalLow} budgetTotalHigh={budget.totalHigh}
          waterPrice={waterPrice} setWaterPrice={setWaterPrice}
          {...stepProps}
        />
      )}
    </div>
  );
};

export default RainwaterHarvestingModule;
